import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramCamera } from "./PlanogramCamera.js";
import { PlanogramCameraFrameAttributes } from './Resources.js';
import { PlanogramContainer } from './PlanogramContainer.js';

import * as THREE from 'three';

export class PlanogramCameraFrame extends HTMLElement {

    //#region Attributes
    static Attributes = PlanogramCameraFrameAttributes.Attributes;
    static UnobservedAttributes = { AttachedFrame: 'attached-frame' }

    static get observedAttributes() {
        return [...Object.values(PlanogramCameraFrame.Attributes)];
    }
    //#endregion

    //#region Attribute Properties
    get childFrameDirection() {
        return this.hasAttribute(PlanogramCameraFrame.Attributes.ChildFrameDirection) ?
            Number(this.getAttribute(PlanogramCameraFrame.Attributes.ChildFrameDirection)) : PlanogramCameraFrame.frameType.Horizontal;
    }
    get size() { return Number(this.getAttribute(PlanogramCameraFrame.Attributes.Size)) || 1; }
    // get frameId() { return this.getAttribute(PlanogramCameraFrame.Attributes.FrameId); } // Deprecated: use UID
    get noResize() { return this.hasAttribute(PlanogramCameraFrame.Attributes.NoResize); }
    get noTab() { return this.hasAttribute(PlanogramCameraFrame.Attributes.NoTab); }
    get fixed() { return this.hasAttribute(PlanogramCameraFrame.Attributes.Fixed); }
    //#endregion

    //#region Public Properties
    //camera order matters - cameras are now direct children of the frame
    /**@type {PlanogramCamera[]} */
    get associatedCameras() {
        return [...this.children].filter(x => x instanceof PlanogramCamera);
    };
    /**@type {PlanogramCameraFrame[]} */
    get childFrames() {
        return [...this.children].filter(x => x instanceof PlanogramCameraFrame);
    };
    get tabControl() { return this.#tabControl; }
    get uid() { return this.#uid; }
    get isFocused() { return this.associatedCameras.some(x => x.focused); }
    get hasTabBar() { return this.#tabBar !== null; }
    //#endregion

    //#region Private Properties
    /**@type {HTMLDivElement} */
    #tabControl;
    /**@type {HTMLDivElement} */
    #tabBar;
    /**@type {HTMLDivElement} */
    #tabContainer;
    /**@type {HTMLDivElement} */
    #toolBar;
    /**@type {PlanogramCameraFrame} */
    #parentFrame;
    #firstRender = true;
    #uid = PlanogramCameraFrame.#generateUID();
    //#endregion

    constructor() { super(); }

    //#region Camera Methods
    /**
     * Add cameras to this frame only if there are no child frames
     * @param  {...PlanogramCamera} cameras 
     */
    addCameras() {
        this.updateTabItems();
    }
    //#endregion

    //#region Child Frame Methods

    /**
     * 
     * @param {PlanogramCameraFrame} frame 
     */
    addChildFrame(frame) {
        var isEmpty = this.childFrames.length === 0;
        if (isEmpty) this.disposeTabBar();
        this.#tabContainer.appendChild(frame.#tabControl);
        this.updateInternalResizeBorders();
        this.updateSlots();
    }

    updateSlots() {
        const renderer = PlanogramRenderer.get(this);
        renderer.updateSlots();
    }

    /**
     * after removal if there is only one child frame left then move all the cameras to this frame and dispose of the remaining child frame
     */
    removeChildFrame() {
        if (this.childFrames.length === 0) {
            this.disposeResizeBars();
            this.createTabBar();
        }
        this.updateInternalResizeBorders();
    }

    updateChildFrameDirection() {
        this.childFrames.forEach(x => {
            const newDirection = this.childFrameDirection === PlanogramCameraFrame.frameType.Horizontal ?
                PlanogramCameraFrame.frameType.Vertical : PlanogramCameraFrame.frameType.Horizontal;
            x.setAttribute(PlanogramCameraFrame.Attributes.ChildFrameDirection, newDirection);
        });

        if (this.childFrameDirection === PlanogramCameraFrame.frameType.Vertical)
            this.#tabContainer.classList.add("vertical");
        else
            this.#tabContainer.classList.remove("vertical");
        this.updateInternalResizeBorders();

        if (this.updateResizeBorder) this.updateResizeBorders();
    }
    //#endregion

    //#region Public Methods

    getViewBox() {
        if(!this.#tabContainer) return new THREE.Vector4(0, 0, 0, 0);
        const rect = this.#tabContainer.getBoundingClientRect();
        const renderer = PlanogramRenderer.get(this);
        const offset = renderer ? renderer.domElement.getBoundingClientRect() : { x: 0, y: 0 };
        return new THREE.Vector4(rect.x - offset.x, rect.y - offset.y, rect.width, rect.height);
    }

    containsTabItem(tabItem) { return this.#tabBar.contains(tabItem); }
    canSplit(tabItem) {
        const containsTabItem = this.containsTabItem(tabItem);
        return (containsTabItem && this.associatedCameras.length > 1) || (!containsTabItem && this.associatedCameras.length > 0);
    }

    isSiblingFrame(tabItem) {
        // Get only direct sibling frames (children of the same parent)
        var siblingFrames = [...this.parentElement.children].filter(x => x instanceof PlanogramCameraFrame && x !== this);
        const targetFrame = siblingFrames.find(x => x.containsTabItem(tabItem));
        return targetFrame && targetFrame.associatedCameras.length === 1;
    }

    showHideTabBar(hide = false) {
        if (hide) {
            this.disposeTabBar();
        } else if (!hide) {
            this.createTabBar();
            this.updateTabItems();
        }
    }

    static #generateUID() { return Math.random().toString(36).substring(2, 9); }

    /**
     * Set the size of the frame
     */
    setSize() {
        const numeric = parseFloat(this.size);
        if (!isNaN(numeric) && this.tabControl) {
            if (this.fixed) {
                this.tabControl.style.removeProperty("flex-grow");
                this.tabControl.style.flex = `0 0 ${numeric}px`;
            } else {
                this.tabControl.style.removeProperty("flex");
                this.tabControl.style.flexGrow = numeric;
            }
        }
    }

    updateTabItems() {
        if (!this.isConnected || this.associatedCameras.length === 0) return;
        if (!this.#tabBar) {
            this.createTabBar();
        } else
            this.#tabBar.innerHTML = "";
        // Cameras are ordered by their position in the DOM (as children of this frame)
        var ordered = this.associatedCameras;
        ordered.forEach((x, index) => { 
            x.index = index; // Set the camera's index based on DOM position
            if (x.addToTabBar) x.addToTabBar(this.#tabBar);
        });
        this.updateSlots();
    }
    //#endregion

    //#region DOM Methods

    updateFocus() {
        if (this.isFocused)
            this.#tabControl.classList.add("focused");
        else
            this.#tabControl.classList.remove("focused");
    }

    /**
     * Creates the internal resize borders for the child frames
     */
    updateInternalResizeBorders() {
        const tabResizeClass = "tabResizeBar";
        const tabControlClass = "tabControl";
        if (this.#tabContainer) {
            [...this.#tabContainer.children].filter(x => x.classList.contains(tabResizeClass)).forEach(x => x.remove());
            [...this.#tabContainer.children].filter(x => x.classList.contains(tabControlClass)).slice(1).
                forEach((control, i) => {
                    const border = document.createElement("div");
                    border.classList.add(tabResizeClass);
                    control.insertAdjacentElement("beforebegin", border);
                });
        }
    }

    createToolBar() {
        const activeCamera = this.associatedCameras.find(x => x.active);
        const container = activeCamera?.container;
        if (container && container.toolBar) {
            if (this.#toolBar) {
                const slot = this.#toolBar.querySelector("slot");
                slot.assign(container);
            } else {
                this.#toolBar = document.createElement("div");
                this.#toolBar.style = "pointer-events:all;";
                const slot = document.createElement("slot");
                this.#toolBar.appendChild(slot);
                this.#tabControl.insertBefore(this.#toolBar, this.#tabContainer);
                slot.assign(container);
            }
        } else {
            if (this.#toolBar) {
                this.#toolBar.remove();
                this.#toolBar = null;
            }
        }
    }

    /**
     * Create a tab control for the frame
     */
    createTabControl() {

        this.#tabControl = document.createElement("div");
        this.#tabControl.classList.add("tabControl");
        if (!(this.parentElement instanceof PlanogramCameraFrame)) this.#tabControl.classList.add("root");
        this.#tabControl.setAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame, this.#uid);

        this.#tabContainer = document.createElement("div");
        this.#tabContainer.classList.add("tabContainer");
        this.#tabContainer.setAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame, this.#uid);
        if (this.childFrameDirection === PlanogramCameraFrame.frameType.Vertical)
            this.#tabContainer.classList.add("vertical");

        this.#tabControl.appendChild(this.#tabContainer);

        if (this.parentElement instanceof PlanogramCameraFrame) {
            this.parentElement.addChildFrame(this);
        }
        else if (this.parentElement instanceof PlanogramRenderer) {
            this.parentElement.renderView.appendChild(this.#tabControl);
        }
        this.createTabBar();
        this.createToolBar();
        this.updateTabItems();
        this.setResizeHandler();
    }

    setResizeHandler() {
        if (!this.tabControl || this.noResize) return;
        if (this.#tabControl.classList.contains("root")) {
            this.#tabControl.addEventListener("mousedown", down => {
                if (!down.target.classList.contains("tabResizeBar")) return;
                down.preventDefault();
                down.stopPropagation();
                const target = down.target;
                const parent = target.parentElement;
                const isHorizontal = !parent.classList.contains("vertical");
                const childControlInfo = [...parent.children].filter(x => x.classList.contains("tabControl")).
                    map(x => { return { grow: parseFloat(window.getComputedStyle(x).flexGrow), rect: x.getBoundingClientRect(), child: x }; });

                const totalSize = childControlInfo.reduce((a, c) => a + (isHorizontal ? c.rect.width : c.rect.height), 0);
                const totalFlexGrow = childControlInfo.reduce((a, c) => a + c.grow, 0);
                const downPosition = isHorizontal ? down.clientX : down.clientY;
                const mouseMove = (move) => {
                    const movePosition = isHorizontal ? move.clientX : move.clientY;
                    const delta = movePosition - downPosition;
                    childControlInfo.forEach(info => {
                        if (info.child === target.previousElementSibling)
                            info.child.style.flexGrow = `${info.grow + delta / totalSize * totalFlexGrow}`;
                        else if (info.child === target.nextElementSibling)
                            info.child.style.flexGrow = `${info.grow - delta / totalSize * totalFlexGrow}`;
                        else
                            info.child.style.flexGrow = `${info.grow}`;
                    });
                };
                const mouseUp = () => {
                    this.#tabControl.parentElement.removeEventListener("mousemove", mouseMove);
                    this.#tabControl.parentElement.removeEventListener("mouseup", mouseUp);
                    [...parent.children].filter(x => x.classList.contains("tabControl")).forEach(element => {
                        const newSize = parseFloat(window.getComputedStyle(element).flexGrow);
                        const uid = element.getAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame);
                        if (uid) {
                            /**@type {PlanogramCameraFrame} */
                            const frame = [...this.getRootNode().querySelectorAll("planogram-camera-frame")].find(x => x.uid === uid);
                            if (frame) {
                                const raiseResize = frame.size !== newSize;
                                frame.setAttribute(PlanogramCameraFrame.Attributes.Size, newSize);
                                if (raiseResize) frame.raiseResizeEvent();
                            }
                        }
                    });
                };
                this.#tabControl.parentElement.addEventListener("mousemove", mouseMove);
                this.#tabControl.parentElement.addEventListener("mouseup", mouseUp);
            });
        }
    }

    createTabBar() {
        const renderer = PlanogramRenderer.get(this);
        if (!this.#tabBar && this.#tabContainer && this.childFrames.length === 0 && renderer.hideTabs === false && !this.noTab) {
            this.#tabBar = document.createElement("div");
            this.#tabBar.setAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame, this.#uid);
            this.#tabBar.classList.add("tabBar");
            this.#tabBar.addEventListener("click", e => {
                e.stopPropagation();
                if (this.associatedCameras.every(x => !x.focused) && this.associatedCameras.some(x => x.active))
                    this.associatedCameras.find(x => x.active)?.raiseFocusEvent();
            });
            this.#tabControl.insertBefore(this.#tabBar, this.#tabContainer);
        }
    }

    //#region Disposal Methods
    disposeTabControl() {
        this.#tabControl?.remove();
        this.#tabControl = null;
        this.#tabContainer?.remove();
        this.#tabContainer = null;
    }

    disposeTabBar() {
        this.#tabBar?.remove();
        this.#tabBar = null;
    }

    disposeResizeBars() {
        this.#tabContainer.querySelectorAll(".tabResizeBar").forEach(x => x.remove());
    }

    dispose() {
        this.disposeResizeBars();
        this.disposeTabControl();
        this.disposeTabBar();
        if (this.#parentFrame) {
            this.#parentFrame.updateInternalResizeBorders();
            this.#parentFrame.updateTabItems();
            this.#parentFrame.setResizeHandler();
            if (this.#parentFrame.childFrames.length === 0)
                this.#parentFrame.createTabBar();
        }
        this.#parentFrame = null;
    }
    //#endregion

    //#endregion

    //#region Event Raise Methods

    raiseResizeEvent() {
        const resize = new DragEvent("drop", { bubbles: true, cancelable: true, view: window, clientX: 0, clientY: PlanogramCameraFrame.eventType.Resize, dataTransfer: new DataTransfer() });
        resize.dataTransfer.setData(this.size, this.size);
        if (this.dispatched(resize)) {
            console.groupCollapsed("Frame Resize");
            console.log("Target: ", this);
            console.log("Event: ", resize);
            console.log("Size: ", this.size);
            console.groupEnd();
        }
    }

    /**
     * 
     * @param {number} anchor Direction of the anchor
     * @param {string} uid Unique identifier of the camera to move 
     */
    raiseDropEvent(anchor, uid) {
        const drop = new CustomEvent(PlanogramCamera.customEvents.TabMoved, { bubbles: true, cancelable: true, view: window, detail: { anchor, uid } });
        if (this.dispatched(drop)) {
            console.groupCollapsed("Tab Moved");
            console.log("Target: ", this);
            console.log("Event: ", drop);
            console.log("Direction: ", anchor);
            console.log("Anchor Id: ", uid);
            console.groupEnd();
        }
    }


    raiseClickEvent() {
        const click = new MouseEvent("click", { bubbles: true, cancelable: true, view: window });
        if (this.dispatched(click)) {
            console.groupCollapsed("Planogram Frame Tab Clicked");
            console.log("Target: ", this);
            console.log("Event: ", click);
            console.groupEnd();
        }
    }

    dispatched(event) {
        Object.defineProperty(event, 'target', { ...Object.getOwnPropertyDescriptor(event, 'target'), get: () => { return this; } });
        const renderer = PlanogramRenderer.get(this)?.showMessages ?? false;
        const dropped = this.dispatchEvent(event);
        return dropped && renderer;
    }
    //#endregion

    //#region WebComponent Lifecycle CallBacks
    connectedCallback() {

        if (this.parentElement instanceof PlanogramCameraFrame) {
            this.#parentFrame = this.parentElement;
            this.setAttribute(PlanogramCameraFrame.Attributes.ChildFrameDirection, this.parentElement.childFrameDirection === PlanogramCameraFrame.frameType.Horizontal ?
                PlanogramCameraFrame.frameType.Vertical : PlanogramCameraFrame.frameType.Horizontal);
            this.parentElement.disposeTabBar();
        }
        this.createTabControl();
        this.updateTabItems();
        this.setSize();
        this.#firstRender = false;
        const TabMoved = PlanogramCamera.customEvents.TabMoved;
        PlanogramRenderer.applyToRelay(this, { TabMoved });
    }

    disconnectedCallback() {
        this.dispose();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.#firstRender || oldValue === newValue) return;
        switch (name) {
            case PlanogramCameraFrame.Attributes.ChildFrameDirection:
                this.updateChildFrameDirection();
                break;
            case PlanogramCameraFrame.Attributes.Size:
                this.setSize();
                break;
            // Removed: FrameId case - no longer used
            case PlanogramCameraFrame.Attributes.NoResize:
                if (this.noResize) this.setResizeHandler();
                break;
            case PlanogramCameraFrame.Attributes.NoTab:
                this.showHideTabBar(newValue !== null);
                break;
            case PlanogramCameraFrame.Attributes.Fixed:
                this.setSize();
                break;
        }
    }
    //#endregion

    //#region Static Properties
    static frameType = {
        Horizontal: 0,
        Vertical: 1,
    }

    static eventType = {
        Resize: 0,
        Drop: 1,
    }
    //#endregion

}